import { faSave } from '@fortawesome/free-regular-svg-icons'
import { faBan, faCoins, faStream, faUndoAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppContext from '../../../gateway/Authentication/AppContext'
import Authorization from '../../../gateway/Authentication/Authorization'
import AuthService from '../../../gateway/Authentication/AuthService'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import { ConfirmDialogProps } from '../../../gateway/components/ConfirmDialog'
import DataTable, { CaptionButton } from '../../../gateway/components/DataTable'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import ProtectedButton from '../../../gateway/components/ProtectedButton'
import Loading from '../../../gateway/layout/Loading'
import { formatMssqlPloicyStatus } from '../../../gateway/utils/DatatableUtil'
import MssqlCachingService from '../service/MssqlCachingService'
import MssqlIndexingService from '../service/MssqlIndexingService'
import MssqlPolicyService from '../service/MssqlPolicyService'

const MssqlStatus: FC<{}> = () => {
  const { t } = useTranslation()
  const [status, setStatus] = useState({} as any)

  const [loading, setLoading] = useState(true)
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    MssqlPolicyService.getStatus()
      .then((status) => setStatus(status))
      .finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('mssqlAdapter.schema'),
        dataField: 'schema',
        sort: true,
      },
      {
        text: t('mssqlAdapter.table'),
        dataField: 'table',
        sort: true,
      },
      {
        text: t('mssqlAdapter.columnName'),
        dataField: 'columnName',
        sort: true,
      },
      {
        text: t('mssqlAdapter.status'),
        dataField: 'status',
        formatter: (cell: any) => formatMssqlPloicyStatus(cell),
        sort: true,
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.services.mssql.edit),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        classes: 'text-center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.services.mssql.edit}
                shouldRender={row.cachingSupported}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('mssqlAdapter.cacheConfig')}
                onClick={() => configureCacheAfterFetch(row, t, editDialogPropsSetter)}>
                <FontAwesomeIcon icon={faCoins} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.services.mssql.edit}
                shouldRender={!row.indexed && row.indexingSupported === 'true'}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('mssqlAdapter.enableIndex')}
                onClick={() => enableIndex(row, t, confirmDialogPropsSetter)}>
                <FontAwesomeIcon icon={faStream} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.services.mssql.edit}
                shouldRender={row.indexed && row.indexingSupported === 'true'}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('mssqlAdapter.rebuildIndex')}
                onClick={() => rebuildIndex(row, t, confirmDialogPropsSetter)}>
                <FontAwesomeIcon icon={faUndoAlt} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.services.mssql.edit}
                shouldRender={row.indexed && row.indexingSupported === 'true'}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('mssqlAdapter.disableIndex')}
                onClick={() => disableIndex(row, t, confirmDialogPropsSetter)}>
                <FontAwesomeIcon icon={faBan} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, confirmDialogPropsSetter, editDialogPropsSetter, userPermissions])

  var captionButtons: CaptionButton[] = []
  if (status[0]?.databaseAvailable && status[0]?.applyPolicyAvailable) {
    captionButtons.push({
      onClick: () => applyPolicy(t, confirmDialogPropsSetter),
      text: t('mssqlAdapter.applyPolicy'),
      permissions: Authorization.services.mssql.edit,
      icon: faSave,
    })
  }
  if (status[0]?.databaseAvailable && status[0]?.unapplyPolicyAvailable) {
    captionButtons.push({
      onClick: () => unApplyPolicy(t, confirmDialogPropsSetter),
      text: t('mssqlAdapter.unapplyPolicy'),
      permissions: Authorization.services.mssql.edit,
      icon: faUndoAlt,
    })
  }

  return (
    <Loading isLoading={loading}>
      {!status[0]?.databaseAvailable && status[0]?.errorMessage?.length > 0 && (
        <>
          <Container>
            <Row>
              <Col>
                <h3 className='text-danger text-justify'>{status[0]?.errorMessage}</h3>
              </Col>
            </Row>
          </Container>
          <br />
        </>
      )}
      {status[0]?.columnPolicyStatus !== undefined && (
        <DataTable
          keyField='policyId'
          data={status[0]?.columnPolicyStatus} //currently only 1 database is suppoted. [0] logic has to be changed if more databases are supported in the future.
          captionButtons={captionButtons}
          columns={columns}
          caption={t('mssqlAdapter.status')}
        />
      )}
    </Loading>
  )
}

async function configureCacheAfterFetch(row: any, t: TFunction, editDialogPropsSetter: any) {
  const cacheConfig = await MssqlCachingService.getCacheConfig(row.policyId)
  editcacheConfiguration(cacheConfig, t, editDialogPropsSetter)
}

function editcacheConfiguration(cacheConfig: any, t: TFunction, editDialogPropsSetter: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('mssqlAdapter.cacheConfig'),
    onSubmit: MssqlCachingService.setCacheConfig,
    onSubmitExtraArgs: [cacheConfig.policyId, t],
    data: cacheConfig?.cacheUserList.map((userCache: any) => {
      return {
        id: userCache.name,
        type: 'number',
        label: userCache.name,
        value: userCache.size,
      }
    }),
    labelGridSize: 5,
  }
  editDialogPropsSetter(editDialogProps)
}

function applyPolicy(t: TFunction, confirmDialogPropsSetter: any) {
  const enableIndexDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('mssqlAdapter.applyPolicy'),
    body: t('mssqlAdapter.applyPolicyBody'),
    submitText: t('yes'),
    onSubmit: MssqlPolicyService.applyPolicy,
    onSubmitExtraArgs: [t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(enableIndexDialogProps)
}

function unApplyPolicy(t: TFunction, confirmDialogPropsSetter: any) {
  const enableIndexDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('mssqlAdapter.unapplyPolicy'),
    body: t('mssqlAdapter.unapplyPolicyBody'),
    submitText: t('yes'),
    onSubmit: MssqlPolicyService.unapplyPolicy,
    onSubmitExtraArgs: [t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(enableIndexDialogProps)
}

function enableIndex(row: any, t: TFunction, confirmDialogPropsSetter: any) {
  const enableIndexDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('mssqlAdapter.enableIndex'),
    body: t('mssqlAdapter.enableIndexBody', { name: `${row.schema}.${row.table}:${row.columnName}-${row.name}` }),
    submitText: t('yes'),
    onSubmit: MssqlIndexingService.enableIndex,
    onSubmitExtraArgs: [row.policyId, t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(enableIndexDialogProps)
}

function rebuildIndex(row: any, t: TFunction, confirmDialogPropsSetter: any) {
  const rebuildIndexDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('mssqlAdapter.rebuildIndex'),
    body: t('mssqlAdapter.rebuildIndexBody', { name: `${row.schema}.${row.table}:${row.columnName}-${row.name}` }),
    submitText: t('yes'),
    onSubmit: MssqlIndexingService.rebuildIndex,
    onSubmitExtraArgs: [row.policyId, t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(rebuildIndexDialogProps)
}

function disableIndex(row: any, t: TFunction, confirmDialogPropsSetter: any) {
  const disableIndexDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('mssqlAdapter.disableIndex'),
    body: t('mssqlAdapter.disableIndexBody', { name: `${row.schema}.${row.table}:${row.columnName}-${row.name}` }),
    submitText: t('yes'),
    onSubmit: MssqlIndexingService.disableIndex,
    onSubmitExtraArgs: [row.policyId, t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(disableIndexDialogProps)
}

export default MssqlStatus
